import produce from "immer"
import type { DrawEvents, DrawMap, DrawOptions, LatLng, LatLngExpression, Layer } from "leaflet"
import L, { Circle, Marker, Point } from "leaflet"
import type { ReactElement } from "react"
import { useEffect, useMemo, useState } from "react"
import { FeatureGroup as FeatureGroupComponent, Pane, useMap } from "react-leaflet"
import { EditControl } from "react-leaflet-draw"
import { connect } from "react-redux"
import { polylineOverwrite, rectangleOverwrite } from "../../../utils/leaflet-overwrites"
import type { RectangleOverwriteLayer } from "../../../utils/leaflet-overwrites"

import { AttachmentPin, DrawTextEditor } from ".."
import type {
  AppState,
  AppThunkDispatch,
  Attachment,
  AttachmentEditorAction,
  IMapDrawing,
  IPolyline,
  IShape,
  MapViewerActions,
  ProjectActions
} from "../../../redux"
import {
  MapViewerPanel,
  attachmentEditorSelectAttachment,
  mapViewerSwitchEditPanel,
  projectAddDrawing,
  projectMoveDrawing,
  projectRemoveDrawing
} from "../../../redux"
import {
  calculatePolylineRelativePositions,
  createNewDrawingId,
  layerToShape,
  moveAndScalePolyline
} from "../../../utils"
import { MapShape } from "../../atoms"

export interface IMapDrawingOwnProps {
  drawings: IMapDrawing[]
  attachments: Attachment[]
  showDrawings: boolean
  hideAttachments?: boolean | undefined
  removePinLocation: (attachment: Attachment) => void
}

export interface IMapDrawingStateProps {
  color: string
  selectedAttachment?: Attachment
  addDrawing: (drawing: IMapDrawing) => void
  removeDrawing: (drawing: IMapDrawing) => void
  editDrawing: (drawing: IMapDrawing) => void
  moveCurrentAttachment: (attachment: Attachment) => void
  enableSelectionTool: () => void
}

export interface IDrawOptions {
  polyline?: DrawOptions.PolylineOptions | boolean
  polygon?: DrawOptions.PolygonOptions | boolean
  rectangle?: DrawOptions.RectangleOptions | boolean
  circle?: DrawOptions.CircleOptions | boolean
  marker?: DrawOptions.MarkerOptions | boolean
  circlemarker?: DrawOptions.CircleOptions | boolean
}

function MapDrawingView(props: IMapDrawingStateProps & IMapDrawingOwnProps): ReactElement {
  const {
    drawings,
    attachments,
    showDrawings,
    hideAttachments,
    addDrawing,
    color,
    selectedAttachment,
    editDrawing,
    removePinLocation,
    moveCurrentAttachment,
    enableSelectionTool
  } = props
  const [previousEditState, setPreviousEditState] = useState<{ id: string; relativePoints: Point[] }>()

  const map: DrawMap = useMap() as DrawMap
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    map.on(L.Draw.Event.CREATED, processNewShape)

    return () => {
      map.removeEventListener(L.Draw.Event.CREATED, processNewShape)
      map.removeEventListener(L.Draw.Event.EDITMOVE)
      map.removeEventListener(L.Draw.Event.EDITRESIZE)
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    map.removeEventListener(L.Draw.Event.EDITMOVE)
    map.removeEventListener(L.Draw.Event.EDITRESIZE)
    map.on(L.Draw.Event.EDITMOVE, onShapeMoved)
    map.on(L.Draw.Event.EDITRESIZE, onShapeResize)
  }, [drawings, selectedAttachment])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const drawOptions = useMemo<IDrawOptions>(() => {
    return {
      circle: false,
      rectangle: false,
      polygon: false,
      polyline: false,
      circlemarker: false
    }
  }, [color])

  function processNewShape(event: any): void {
    const typedEvent: DrawEvents.Created = event as DrawEvents.Created
    if (typedEvent.layer instanceof Marker) {
      map.removeLayer(typedEvent.layer)
      return
    }

    const shape: IShape = layerToShape(typedEvent.layer)
    enableSelectionTool()
    addDrawing({ shape, isPopupOpen: true, id: createNewDrawingId() })
    map.removeLayer(typedEvent.layer)
  }

  function getActiveDrawing(): IMapDrawing | undefined {
    return drawings.find((drawing: IMapDrawing) => drawing.isPopupOpen)
  }

  function onShapeMoved(event: DrawEvents.EditMove): void {
    if (!(event.layer instanceof L.Marker)) {
      movePopup(event.layer)
    }
    changeShapeBounds(event.layer)
  }

  function onShapeResize(event: DrawEvents.EditResize): void {
    movePopup(event.layer)
    changeShapeBounds(event.layer)
  }

  function movePopup(layer: Layer): void {
    layer.getPopup()?.setLatLng(calculatePopupPosition(layer))
    setTimeout(() => {
      layer.getPopup()?.setLatLng(calculatePopupPosition(layer))
    }, 0)
  }

  function calculatePopupPosition(layer: Layer): LatLngExpression {
    if (layer instanceof rectangleOverwrite) {
      const centerPoint: Point = (layer as any).editing._getCenterPoint() as Point
      const corner1: Point = map.latLngToLayerPoint((layer as RectangleOverwriteLayer)._corner1)

      const corner2: Point = map.latLngToLayerPoint((layer as RectangleOverwriteLayer)._corner2)

      const y: number = Math.min(corner1.y, corner2.y)

      const point: Point = new Point(centerPoint.x, y)
      const coord: LatLng = map.layerPointToLatLng(point)
      return [coord.lat, coord.lng]
    }

    if (layer instanceof Circle) {
      const center: Point = map.latLngToLayerPoint(layer!.getLatLng())
      const radius: number = (layer as any)._radius

      const point: Point = new Point(center.x, center.y - radius)
      const coord: LatLng = map.layerPointToLatLng(point)

      return [coord.lat, coord.lng]
    }

    if (layer instanceof polylineOverwrite) {
      const point1: Point = map.latLngToLayerPoint((layer as any)._latlngs[0])
      const point2: Point = map.latLngToLayerPoint((layer as any)._latlngs[1])

      const maxX: number = Math.max(point1.x, point2.x)
      const minX: number = Math.min(point1.x, point2.x)
      const minY: number = Math.min(point1.y, point2.y)
      const centerX: number = minX + (maxX - minX) / 2

      const coord: LatLng = map.layerPointToLatLng(new Point(centerX, minY))

      return [coord.lat, coord.lng]
    }
    const center: LatLng = (layer as any).getBounds().getCenter()
    const topLat: number = (layer as any).getBounds().getNorthWest().lat

    return [topLat, center.lng]
  }

  function changeShapeBounds(layer: Layer): void {
    const editedDrawing: IMapDrawing | undefined = getActiveDrawing()

    if (layer instanceof L.Marker && selectedAttachment) {
      attachmentMovePin(layer, selectedAttachment)
      return
    }

    if (!editedDrawing) return

    let newShape: IShape = editedDrawing.shape

    if (editedDrawing.shape.type === "polyline") {
      if (previousEditState && previousEditState.id === editedDrawing.id) {
        newShape = moveAndScalePolyline(
          editedDrawing.shape as IPolyline,
          layer as RectangleOverwriteLayer,
          previousEditState.relativePoints,
          map
        )
      }

      const shapeHasChanged: boolean =
        !previousEditState || (previousEditState && previousEditState.id !== editedDrawing.id)

      const relativePoints: Point[] = shapeHasChanged
        ? calculatePolylineRelativePositions(newShape as IPolyline, map)
        : previousEditState!.relativePoints

      setPreviousEditState({
        id: editedDrawing.id,
        relativePoints
      })
    } else {
      newShape = layerToShape(layer)
    }
    const newDrawing: IMapDrawing = produce(editedDrawing, (draft: IMapDrawing) => {
      draft.shape = newShape
    })

    editDrawing(newDrawing)
  }

  function attachmentMovePin(layer: Layer, attachment: Attachment): void {
    const position: LatLng = (layer as L.Marker).getLatLng()
    const newAttachment: Attachment = produce(attachment, (draft: Attachment) => {
      draft.pointOnMap = {
        lat: position.lat,
        lng: position.lng
      }
    })
    moveCurrentAttachment(newAttachment)
  }

  return (
    <FeatureGroupComponent>
      <EditControl position="topleft" draw={drawOptions} />
      <Pane name="map-drawing-pane">
        {showDrawings &&
          drawings.map((drawing: IMapDrawing) => <MapShape key={`shape-${drawing.id}`} drawing={drawing} />)}
      </Pane>

      {(!hideAttachments || selectedAttachment) &&
        attachments.map((attachment: Attachment, index: number) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in a later PR
          <AttachmentPin key={index} attachment={attachment} removePin={removePinLocation} />
        ))}
      <DrawTextEditor />
    </FeatureGroupComponent>
  )
}

function mapStateToProps(state: AppState) {
  return {
    color: state.colorPicker.color,
    showAttachments: state.mapViewer.panels.get(MapViewerPanel.Attachement) || false,
    selectedAttachment: state.attachmentsEditor.currentAttachment
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ProjectActions | AttachmentEditorAction | MapViewerActions>) {
  return {
    addDrawing: (drawing: IMapDrawing) => dispatch(projectAddDrawing(drawing)),
    removeDrawing: (drawing: IMapDrawing) => dispatch(projectRemoveDrawing(drawing)),
    editDrawing: (drawing: IMapDrawing) => dispatch(projectMoveDrawing(drawing)),
    moveCurrentAttachment: (attachment: Attachment) => dispatch(attachmentEditorSelectAttachment(attachment)),
    enableSelectionTool: () => dispatch(mapViewerSwitchEditPanel(MapViewerPanel.Selection))
  }
}

export const MapDrawing = connect(mapStateToProps, mapDispatchToProps)(MapDrawingView)
